import {
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Tag,
  notification,
} from "antd";
import cuid from "cuid";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { PRICER_TYPES, PRICING_MODES, TAG_COLORS } from "utils/constants";

const SelectCoverages = ({ values, setFieldValue, data }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [configDrawerIndex, setConfigDrawerIndex] = useState(null);
  const [form] = Form.useForm();
  const isComposable = get(values, "pricer.type") === PRICER_TYPES.COMPOSABLE;
  const planId = data.id;

  const { data: categoriesData, loading } = useQuery(
    graphql.queries.COVERAGE_CATEGORIES,
    { variables: {}, fetchPolicy: "network-only" },
  );

  const [addCoverage] = useMutation(
    graphql.mutations.CREATE_INSURANCE_PLAN_COVERAGE,
  );

  const [deleteCoverage] = useMutation(
    graphql.mutations.DELETE_INSURANCE_PLAN_COVERAGE,
  );

  const [updateCoverage] = useMutation(
    graphql.mutations.UPDATE_INSURANCE_PLAN_COVERAGE,
  );

  const [addPricer] = useMutation(graphql.mutations.CREATE_PRICER);
  const [updatePricer] = useMutation(graphql.mutations.UPDATE_PRICER);

  const [updateCategoryMutation] = useMutation(
    graphql.mutations.UPDATE_COVERAGE_CATEGORY,
  );

  const [createCategory, { loading: creating }] = useMutation(
    graphql.mutations.CREATE_COVERAGE_CATEGORY,
    {
      refetchQueries: [
        { query: graphql.queries.COVERAGE_CATEGORIES, variables: {} },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        form.resetFields();
        setDrawerOpen(false);
      },
    },
  );

  const categories = get(categoriesData, "coverageCategories.data") || [];
  const selectedCoverages = get(values, "insurancePlanCoverages") || [];
  const selectedIds = selectedCoverages.map((c) =>
    get(c, "coverageCategory.id"),
  );

  const availableCategories = categories.filter(
    (cat) =>
      !selectedIds.includes(cat.id) &&
      (cat.label || "").toLowerCase().includes(search.toLowerCase()),
  );

  const handleAddCoverage = async (category) => {
    const id = cuid();
    const needsId = cuid();
    const newCoverage = {
      id,
      coverageCategory: category,
      needs: { id: needsId, level: 1 },
      pricer: null,
      coverageMappings: [],
    };

    try {
      await addCoverage({
        variables: {
          data: {
            id,
            insurancePlan: { id: planId },
            coverageCategory: { id: category.id },
            needs: { id: needsId, level: 1 },
          },
        },
      });
      setFieldValue("insurancePlanCoverages", [
        ...selectedCoverages,
        newCoverage,
      ]);
      notification.success({
        message: t("CONFIGURE.NOTIFICATIONS.COVERAGE_ADDED", {
          label: category.label,
        }),
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.COVERAGE_ADD_FAILED"),
      });
    }
  };

  const handleRemoveCoverage = async (index) => {
    const coverage = selectedCoverages[index];
    try {
      await deleteCoverage({
        variables: { where: { id: coverage.id } },
      });
      setFieldValue(
        "insurancePlanCoverages",
        selectedCoverages.filter((_, i) => i !== index),
      );
      notification.success({
        message: t("CONFIGURE.NOTIFICATIONS.COVERAGE_REMOVED"),
      });
    } catch (err) {
      notification.error({
        message: t("CONFIGURE.NOTIFICATIONS.DELETE_FAILED"),
      });
    }
  };

  const handleCreateCategory = () => {
    form.validateFields().then(async (formValues) => {
      const categoryId = cuid();
      const coverageId = cuid();
      const needsId = cuid();

      try {
        await createCategory({
          variables: {
            data: {
              id: categoryId,
              value: formValues.value,
              label: formValues.label,
              description: formValues.description,
              order: parseInt(formValues.order, 10) || 1,
              weight: parseInt(formValues.weight, 10) || 1,
            },
          },
        });

        await addCoverage({
          variables: {
            data: {
              id: coverageId,
              carenceDays: parseInt(formValues.carenceDays, 10) || null,
              insurancePlan: { id: planId },
              coverageCategory: { id: categoryId },
              needs: { id: needsId, level: 1 },
            },
          },
        });

        setFieldValue("insurancePlanCoverages", [
          ...selectedCoverages,
          {
            id: coverageId,
            carenceDays: parseInt(formValues.carenceDays, 10) || null,
            coverageCategory: {
              id: categoryId,
              value: formValues.value,
              label: formValues.label,
              icon: null,
              order: parseInt(formValues.order, 10) || 1,
              weight: parseInt(formValues.weight, 10) || 1,
            },
            needs: { id: needsId, level: 1 },
            pricer: null,
            coverageMappings: [],
          },
        ]);

        form.resetFields();
        setDrawerOpen(false);
        notification.success({
          message: t("CONFIGURE.NOTIFICATIONS.COVERAGE_CREATED", {
            label: formValues.label,
          }),
        });
      } catch (err) {
        notification.error({
          message: t("CONFIGURE.NOTIFICATIONS.COVERAGE_CREATE_FAILED"),
        });
      }
    });
  };

  const getPricingModeLabel = (coverage) =>
    get(coverage, "pricer.type") === PRICER_TYPES.COMPOSABLE
      ? t("CONFIGURE.SELECT_COVERAGES.PRICING_COMPOSABLE")
      : t("CONFIGURE.SELECT_COVERAGES.PRICING_PACKAGED");

  return (
    <div className="configure-step">
      <div className="configure-layout--split">
        <Card
          title={t("CONFIGURE.SELECT_COVERAGES.AVAILABLE")}
          className="configure-layout__panel"
          extra={
            <div className="d-flex gap-8">
              <Input.Search
                placeholder={t("CONFIGURE.SELECT_COVERAGES.SEARCH")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                className="configure-field--search"
              />
              <Button
                type="primary"
                icon={<BiPlus size={16} />}
                onClick={() => setDrawerOpen(true)}
              >
                {t("CONFIGURE.SELECT_COVERAGES.NEW")}
              </Button>
            </div>
          }
        >
          {loading ? (
            <div className="configure-loading">
              {t("CONFIGURE.SELECT_COVERAGES.LOADING")}
            </div>
          ) : availableCategories.length === 0 ? (
            <Empty
              description={
                categories.length === 0
                  ? t("CONFIGURE.SELECT_COVERAGES.EMPTY_CATALOG")
                  : t("CONFIGURE.SELECT_COVERAGES.ALL_SELECTED")
              }
            />
          ) : (
            <div className="configure-category-grid">
              {availableCategories.map((category) => (
                <Card
                  key={category.id}
                  size="small"
                  hoverable
                  className="configure-category-card"
                  onClick={() => handleAddCoverage(category)}
                >
                  <div className="d-flex gap-12 align--center">
                    <div className="configure-category-avatar">
                      {(category.label ||
                        category.value ||
                        "?")[0].toUpperCase()}
                    </div>
                    <div className="flex--grow-1">
                      <div className="configure-name">
                        {category.label || category.value}
                      </div>
                      {category.description && (
                        <div className="configure-meta">
                          {category.description}
                        </div>
                      )}
                    </div>
                    <BiPlus size={20} className="configure-icon-primary" />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        <Card
          title={
            <span>
              {t("CONFIGURE.SELECT_COVERAGES.SELECTED")}{" "}
              <Tag color={TAG_COLORS.BLUE}>{selectedCoverages.length}</Tag>
            </span>
          }
          className="configure-layout__panel"
        >
          {selectedCoverages.length === 0 ? (
            <Empty
              description={t("CONFIGURE.SELECT_COVERAGES.EMPTY_SELECTED")}
            />
          ) : (
            <div className="configure-stack">
              {selectedCoverages.map((coverage, index) => {
                const cat = get(coverage, "coverageCategory", {});
                return (
                  <Card
                    key={coverage.id}
                    size="small"
                    className="configure-category-card--static"
                  >
                    <div className="d-flex justify--between align--center">
                      <div className="d-flex gap-12 align--center">
                        <div className="configure-category-avatar">
                          {(cat.label || cat.value || "?")[0].toUpperCase()}
                        </div>
                        <div className="configure-name">
                          {cat.label || cat.value}
                        </div>
                      </div>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<BiTrash size={16} />}
                        onClick={() => handleRemoveCoverage(index)}
                      />
                    </div>
                    <div className="m-top-8">
                      <Button
                        type="link"
                        size="small"
                        className="configure-link-btn"
                        onClick={() => setConfigDrawerIndex(index)}
                      >
                        {t("CONFIGURE.SELECT_COVERAGES.CONFIGURE")}
                        {get(coverage, "carenceDays")
                          ? ` · ${t("CONFIGURE.SELECT_COVERAGES.WAITING_PERIOD", {
                              days: get(coverage, "carenceDays"),
                            })}`
                          : ""}
                        {` · ${getPricingModeLabel(coverage)}`}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Drawer
        title={
          configDrawerIndex !== null
            ? t("CONFIGURE.SELECT_COVERAGES.DRAWER_CONFIGURE", {
                label: get(
                  selectedCoverages,
                  `${configDrawerIndex}.coverageCategory.label`,
                  "",
                ),
              })
            : ""
        }
        open={configDrawerIndex !== null}
        onClose={() => setConfigDrawerIndex(null)}
        width={400}
      >
        {configDrawerIndex !== null &&
          (() => {
            const coverage = selectedCoverages[configDrawerIndex];
            const cat = get(coverage, "coverageCategory", {});
            const idx = configDrawerIndex;

            const updateCoverageField = async (field, value) => {
              const updated = [...selectedCoverages];
              updated[idx] = { ...updated[idx], [field]: value };
              setFieldValue("insurancePlanCoverages", updated);
            };

            const saveCoverageField = async (field) => {
              try {
                await updateCoverage({
                  variables: {
                    where: { id: coverage.id },
                    data: { [field]: get(coverage, field) },
                  },
                });
              } catch (err) {
                notification.error({
                  message: t("CONFIGURE.NOTIFICATIONS.UPDATE_FAILED"),
                });
              }
            };

            const updateCatField = (field, value) => {
              const updated = [...selectedCoverages];
              updated[idx] = {
                ...updated[idx],
                coverageCategory: { ...cat, [field]: value },
              };
              setFieldValue("insurancePlanCoverages", updated);
            };

            const saveCatField = async (field) => {
              try {
                await updateCategoryMutation({
                  variables: {
                    where: { id: cat.id },
                    data: { [field]: cat[field] },
                  },
                });
              } catch (err) {
                notification.error({
                  message: t(
                    "CONFIGURE.NOTIFICATIONS.CATEGORY_UPDATE_FAILED",
                  ),
                });
              }
            };

            return (
              <div className="configure-stack configure-stack--md">
                <h4 className="configure-section-title">
                  {t("CONFIGURE.SELECT_COVERAGES.CATEGORY")}
                </h4>
                <div>
                  <label className="input__label">
                    {t("CONFIGURE.SELECT_COVERAGES.LABEL")}
                  </label>
                  <Input
                    value={cat.label}
                    onChange={(e) => updateCatField("label", e.target.value)}
                    onBlur={() => saveCatField("label")}
                  />
                </div>
                <div>
                  <label className="input__label">
                    {t("CONFIGURE.SELECT_COVERAGES.CODE")}
                  </label>
                  <Input
                    value={cat.value}
                    onChange={(e) => updateCatField("value", e.target.value)}
                    onBlur={() => saveCatField("value")}
                  />
                </div>
                <div>
                  <label className="input__label">
                    {t("CONFIGURE.PLAN_DETAILS.DESCRIPTION")}
                  </label>
                  <Input.TextArea
                    value={cat.description || ""}
                    onChange={(e) =>
                      updateCatField("description", e.target.value)
                    }
                    onBlur={() => saveCatField("description")}
                    rows={2}
                  />
                </div>
                <div className="configure-form-row d-flex gap-16">
                  <div className="flex--grow-1">
                    <label className="input__label">
                      {t("CONFIGURE.SELECT_COVERAGES.ORDER")}
                    </label>
                    <InputNumber
                      value={cat.order}
                      min={0}
                      onChange={(val) => updateCatField("order", val)}
                      onBlur={() => saveCatField("order")}
                      className="w-100"
                    />
                  </div>
                  <div className="flex--grow-1">
                    <label className="input__label">
                      {t("CONFIGURE.SELECT_COVERAGES.WEIGHT")}
                    </label>
                    <InputNumber
                      value={cat.weight}
                      min={0}
                      onChange={(val) => updateCatField("weight", val)}
                      onBlur={() => saveCatField("weight")}
                      className="w-100"
                    />
                  </div>
                </div>

                <h4 className="configure-section-title configure-section-title--spaced">
                  {t("CONFIGURE.SELECT_COVERAGES.COVERAGE_PARAMS")}
                </h4>
                <div className="configure-form-row d-flex gap-16">
                  <div className="flex--grow-1">
                    <label className="input__label">
                      {t("CONFIGURE.SELECT_COVERAGES.WAITING_DAYS")}
                    </label>
                    <InputNumber
                      min={0}
                      value={get(coverage, "carenceDays")}
                      onChange={(val) =>
                        updateCoverageField("carenceDays", val)
                      }
                      onBlur={() => saveCoverageField("carenceDays")}
                      className="w-100"
                    />
                  </div>
                </div>
                <div>
                  <label className="input__label">
                    {t("CONFIGURE.SELECT_COVERAGES.PRICING_MODE")}
                  </label>
                  <Radio.Group
                    value={
                      get(coverage, "pricer.type") === PRICER_TYPES.COMPOSABLE
                        ? PRICING_MODES.COMPOSABLE
                        : PRICING_MODES.PACKAGED
                    }
                    onChange={async (e) => {
                      const newType =
                        e.target.value === PRICING_MODES.COMPOSABLE
                          ? PRICER_TYPES.COMPOSABLE
                          : PRICER_TYPES.DB;
                      try {
                        const existingPricerId = get(coverage, "pricer.id");
                        if (existingPricerId) {
                          await updatePricer({
                            variables: {
                              where: { id: existingPricerId },
                              data: { type: newType },
                            },
                          });
                          updateCoverageField("pricer", {
                            ...coverage.pricer,
                            type: newType,
                          });
                        } else {
                          const newPricerId = cuid();
                          await addPricer({
                            variables: {
                              data: {
                                id: newPricerId,
                                type: newType,
                                insurancePlan: { id: planId },
                              },
                            },
                          });
                          await updateCoverage({
                            variables: {
                              where: { id: coverage.id },
                              data: { pricer: { id: newPricerId } },
                            },
                          });
                          updateCoverageField("pricer", {
                            id: newPricerId,
                            type: newType,
                          });
                        }
                      } catch (err) {
                        notification.error({
                          message: t("CONFIGURE.NOTIFICATIONS.UPDATE_FAILED"),
                        });
                      }
                    }}
                  >
                    <Radio.Button value={PRICING_MODES.PACKAGED}>
                      {t("CONFIGURE.SELECT_COVERAGES.PRICING_PACKAGED")}
                    </Radio.Button>
                    <Radio.Button value={PRICING_MODES.COMPOSABLE}>
                      {t("CONFIGURE.SELECT_COVERAGES.PRICING_COMPOSABLE")}
                    </Radio.Button>
                  </Radio.Group>
                </div>
              </div>
            );
          })()}
      </Drawer>

      <Drawer
        title={t("CONFIGURE.SELECT_COVERAGES.NEW_CATEGORY")}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        extra={
          <Button
            type="primary"
            onClick={handleCreateCategory}
            loading={creating}
          >
            {t("CONFIGURE.SELECT_COVERAGES.CREATE")}
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="label"
            label={t("CONFIGURE.SELECT_COVERAGES.LABEL")}
            rules={[
              {
                required: true,
                message: t("CONFIGURE.SELECT_COVERAGES.LABEL_REQUIRED"),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="value"
            label={t("CONFIGURE.SELECT_COVERAGES.CODE")}
            rules={[
              {
                required: true,
                message: t("CONFIGURE.SELECT_COVERAGES.CODE_REQUIRED"),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label={t("CONFIGURE.PLAN_DETAILS.DESCRIPTION")}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <div className="configure-form-row d-flex gap-16">
            <Form.Item
              name="order"
              label={t("CONFIGURE.SELECT_COVERAGES.ORDER")}
              className="flex-field"
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              name="weight"
              label={t("CONFIGURE.SELECT_COVERAGES.WEIGHT")}
              className="flex-field"
            >
              <Input type="number" />
            </Form.Item>
          </div>
          <div className="configure-form-row d-flex gap-16">
            <Form.Item
              name="carenceDays"
              label={t("CONFIGURE.SELECT_COVERAGES.WAITING_DAYS")}
              className="flex-field"
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default SelectCoverages;
